import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';

import useDialogs from '@/hooks/useDialogs';
import {
  getActionsSelector,
  selectDrafDialogActions,
  selectDraftDialogId,
} from '@/redux/dialogs/selectors';
import { selectBrainId } from '@/redux/session/selectors';
import { AuthStepKey } from '@/shared/types/node';

// A node that can't be deleted because something still points at it. For an
// auth sub_agent target we also carry where to deep-link (the exact
// success/failure dropdown); event/jump references only know their dialog.
export type UsedNode = {
  dialogId: string;
  label: string;
  actionId?: string;
  step?: AuthStepKey;
  outcome?: 'success' | 'failure';
};

/**
 * Turns the used-node references into `{ label, href }` for the delete-node
 * warning Banner (pass `referenceProp="href"`). Auth references deep-link to
 * the exact success/failure dropdown and label themselves as such; every other
 * reference links to its dialog by name. Shared by every delete entry point
 * (canvas node + node header menu) so they stay consistent.
 */
export const buildUsedNodeReferences = (
  usedNodes: UsedNode[],
  slug: string | null,
  brainId: string | null,
  t: TFunction
): { label: string; href: string }[] =>
  usedNodes.map((used) => {
    const base = `/${slug}/ai-agents/${brainId}/workflows/dialogs/${used.dialogId}`;
    if (used.actionId && used.outcome) {
      const href = `${base}?${new URLSearchParams({
        action: used.actionId,
        step: used.step ?? 'identification',
        outcome: used.outcome,
      }).toString()}`;
      const labelKey =
        used.outcome === 'success'
          ? 'dialog.delete_node_auth_success_ref'
          : 'dialog.delete_node_auth_failure_ref';
      return { label: t(labelKey, { 0: used.label }), href };
    }
    return { label: used.label, href: base };
  });

const useNodesConnected = () => {
  const brainId = useSelector(selectBrainId);
  const { dialogs } = useDialogs(brainId);
  const allActions = getActionsSelector(dialogs);
  const drafDialogActions = useSelector(selectDrafDialogActions);
  const draftDialogId = useSelector(selectDraftDialogId);

  // Filter out actions that are not in the draft dialog
  const finalActions = allActions?.filter((action) => {
    if (action.dialog_id === draftDialogId) {
      return drafDialogActions.find((draftAction) => draftAction.action_id === action.action_id);
    }
    return true;
  });

  const findUsedNodes = (nodeId: string): UsedNode[] => {
    // One entry per dialog (keeps the warning's "following dialogs" framing),
    // but prefer an auth deep-link over a plain dialog link when both point at
    // the same dialog.
    const byDialog = new Map<string, UsedNode>();

    finalActions.forEach((action) => {
      // An event action references a node via trigger_node_id; an auth
      // sub_agent references it as a success/failure target. Either makes the
      // node "used" — and for auth we also learn which dropdown to deep-link.
      const targets = action.auth_targets;
      let outcome: 'success' | 'failure' | undefined;
      if (targets?.failure_node_id === nodeId) {
        outcome = 'failure';
      } else if (targets?.success_node_id === nodeId) {
        outcome = 'success';
      }
      const isEventRef = action.trigger_node_id === nodeId;
      if (!isEventRef && !outcome) {
        return;
      }

      const ref: UsedNode =
        outcome && targets
          ? {
              dialogId: action.dialog_id,
              label: action.dialog_name,
              actionId: action.action_id,
              step: targets.step,
              outcome,
            }
          : { dialogId: action.dialog_id, label: action.dialog_name };

      const existing = byDialog.get(ref.dialogId);
      if (!existing || (!existing.actionId && ref.actionId)) {
        byDialog.set(ref.dialogId, ref);
      }
    });

    return [...byDialog.values()];
  };
  return { findUsedNodes };
};

export default useNodesConnected;
